import { useQuery, useQueryClient } from 'react-query'
import { walletApi, userApi } from '@/services/api'

export interface UseWalletsReturn {
  wallets: any[] | undefined
  isLoading: boolean
  totalBalance: number
  primaryWallet: any | undefined
  userCurrency: string
  refetch: () => void
}

export function useWallets(): UseWalletsReturn {
  const queryClient = useQueryClient()

  // Fetch wallets
  const { data: wallets, isLoading } = useQuery(
    'wallets',
    () => walletApi.getWallets().then(res => {
      // Handle different response structures
      const responseData = (res as any).data
      const data = responseData?.data || responseData?.results || responseData
      return Array.isArray(data) ? data : (data?.results || [])
    }),
    { refetchInterval: 30000 } // Refetch every 30 seconds
  )

  // Get user profile to get currency preference
  const { data: userProfile } = useQuery(
    'user-profile',
    async () => {
      const response = await userApi.getUserProfile()
      // /users/me returns user data directly, not in a profiles array
      const profileData = response.data.data || response.data
      return profileData || null
    },
    { enabled: true }
  )

  // Calculate total balance - convert to number if string and sum all wallets
  const totalBalance = wallets?.reduce((sum: number, wallet: any) => {
    let balance: number = 0
    
    if (wallet?.balance !== undefined && wallet?.balance !== null) {
      if (typeof wallet.balance === 'string') {
        balance = parseFloat(wallet.balance) || 0
      } else if (typeof wallet.balance === 'number') {
        balance = wallet.balance
      } else {
        balance = Number(wallet.balance) || 0
      }
    }
    
    const finalBalance = isNaN(balance) ? 0 : balance
    return sum + finalBalance
  }, 0) || 0

  // Get primary wallet and user currency
  const primaryWallet = wallets?.[0]
  const primaryCurrency = primaryWallet?.currency_code || primaryWallet?.currency
  const userCurrency = userProfile?.currency || primaryCurrency || 'EUR'

  // Refetch function
  const refetch = () => {
    queryClient.invalidateQueries('wallets')
    queryClient.invalidateQueries('user-profile')
  }

  return {
    wallets,
    isLoading,
    totalBalance,
    primaryWallet,
    userCurrency,
    refetch,
  }
}

